// One-shot post-login handshake: bundle every PERSONAL lx:* key still sitting
// in this browser, POST to /api/me/preferences/ingest, then clear them. The
// server merges the bundle into Collaborator.preferences (canonical paths)
// and returns the merged object.
//
// Idempotent: a second call finds zero keys and returns null without an HTTP
// request. Safe to call on every login. Failure is non-fatal — keys stay put
// and we retry next login.
//
// This list mirrors server/src/lib/preferencesMigration.js LEGACY_TO_PREF.
// Keep them in sync; the server silently drops keys it doesn't recognise, so
// drift would just leak data.

import { api } from '../api';

export const LEGACY_PERSONAL_KEYS = [
  // M+3.6 — `lx:themes`, `lx:theme`, `lx:navScale` no longer ingested. Themes
  // are firm-wide; nav-scale was retired. Local keys stay put if any browser
  // still has them — they're harmless leftover.
  'lx:themeColor',
  'lx:font',
  'lx:textSize',
  'lx:lang',
  'lx:sc',
  'lx:nwh',
  'lx:workhours',
  'lx:emailclient',
  'lx:mtgapp',
  'lx:mtgplat',
  'lx:mtgdefloc',
  'lx:polltool',
  'lx:pollurl',
  'lx:pomint',
  'lx:maxTimers',
  'lx:timerRound',
  'lx:aglb',
  'lx:agview',
  'lx:agtfilt',
  'lx:agdrange',
  'lx:dl',
  'lx:listsort',
  'lx:casort',
  'lx:dismissed',
  'lx:notifread',
  'lx:colorHistory',
];

function tryParse(raw: any) {
  if (raw == null) return undefined;
  if (typeof raw !== 'string') return raw;
  try { return JSON.parse(raw); } catch { return raw; }
}

async function readKey(key: string) {
  // Most lx:* values are written via window.storage (the Claude.ai artifact
  // polyfill) which delegates to localStorage. Try the async API first;
  // fall back to direct localStorage so we cover both paths.
  if (typeof window !== 'undefined' && window.storage && typeof window.storage.get === 'function') {
    try {
      const r = await window.storage.get(key);
      if (r && r.value !== undefined) return tryParse(r.value);
    } catch {}
  }
  try {
    const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(key) : null;
    return tryParse(raw);
  } catch { return undefined; }
}

async function deleteKey(key: string) {
  if (typeof window !== 'undefined' && window.storage && typeof window.storage.delete === 'function') {
    try { await window.storage.delete(key); } catch {}
  }
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem(key); } catch {}
}

/**
 * Bundle present PERSONAL keys, POST to /api/me/preferences/ingest, then
 * clear them on success. Returns the merged prefs object (server response)
 * on success, or null if there was nothing to ingest or the request failed.
 */
export async function ingestLegacyPrefs() {
  const bundle: Record<string, any> = {};
  for (const key of LEGACY_PERSONAL_KEYS) {
    const v = await readKey(key);
    // Don't ship undefined / null / empty-string — the server already drops
    // those. Sending {} or [] is fine (the user explicitly wanted that state).
    if (v === undefined || v === null || v === '') continue;
    bundle[key] = v;
  }
  if (Object.keys(bundle).length === 0) return null;

  let merged;
  try {
    merged = await api.post('/me/preferences/ingest', bundle);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ingestLegacyPrefs] ingest failed; legacy keys retained for retry next login.', e && (e as any).message);
    return null;
  }

  // Server confirmed ingestion — safe to clear locally.
  for (const key of LEGACY_PERSONAL_KEYS) {
    await deleteKey(key);
  }
  return merged;
}
